import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, ChevronRight, Clock3, Search } from 'lucide-react';
import { isReportId, reportTitleKey, type ReportId } from '@gc/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import { BOX_RESETS_ARCHIVE_PATH } from './BoxResetArchivePage';
import type { LegacySummary } from './LegacyReportView';
import { PAYMENTS_BY_EMPLOYEE_PATH } from './PaymentsByEmployeePage';
import { LAST_REPORT_KEY } from './ReportViewPage';
import { useReportCatalogue } from './useReports';

/**
 * Reports (SPEC §5.7) — the PICKER, and only the picker.
 *
 * Owner, twice: first "make the report type easier to use", then "when i
 * press a report title … take me to a different page where i can see the
 * report and there's will just be a simple back arrow". So this screen does
 * one job: find the report. Every report — the ARCHIVED thirteen from the
 * migrated-off system included — sits in one grouped, searchable list, and
 * tapping a card navigates to `ReportViewPage`, which owns the range
 * controls and the sheet.
 *
 * The catalogue comes from the server so the list cannot drift from the
 * reports that actually exist — a card that 404s is worse than a card that
 * is missing. A "Last viewed" shortcut reopens whatever was read last.
 */

type Choice = { kind: 'live'; id: ReportId } | { kind: 'legacy'; id: string };

/** One card in the list: where it goes and what it says. */
interface ReportCard {
  key: string;
  to: string;
  title: string;
  caption: string | null;
}

export function ReportsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  // SPEC §5.7 — Reports are owner/admin. The API enforces the same key; this
  // stops the screen being reached by typing the URL.
  const allowed = can('reports.view');
  const { catalogue, error: catalogueError } = useReportCatalogue();

  const [legacy, setLegacy] = useState<LegacySummary[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.get<LegacySummary[]>('/reports/legacy');
        if (!cancelled) setLegacy(list);
      } catch {
        // An empty archive is a normal state — a venue that never migrated has
        // none — so this stays quiet and the group simply lists nothing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const open = (choice: Choice) => {
    navigate(choice.kind === 'live' ? `/reports/${choice.id}` : `/reports/archived/${choice.id}`);
  };

  /**
   * The "Last viewed" shortcut — the report the owner opened last, validated
   * against what exists today so a retired report cannot be offered.
   */
  const lastViewed = useMemo((): (Choice & { title: string }) | null => {
    try {
      const raw = localStorage.getItem(LAST_REPORT_KEY);
      if (!raw) return null;
      const last = JSON.parse(raw) as { kind: 'live' | 'legacy'; id: string };
      if (last.kind === 'live' && isReportId(last.id)) {
        for (const entry of catalogue?.sections ?? []) {
          const hit = entry.reports.find((report) => report.id === last.id);
          if (hit) {
            return { kind: 'live', id: hit.id, title: t(reportTitleKey(hit.id), hit.title) };
          }
        }
      }
      if (last.kind === 'legacy') {
        const hit = legacy.find((report) => report.id === last.id);
        if (hit) return { kind: 'legacy', id: hit.id, title: hit.name };
      }
      return null;
    } catch {
      return null;
    }
  }, [catalogue, legacy, t]);

  /**
   * ONE list for every report, grouped under its section heading, filtered by
   * the search box. Groups with nothing matching disappear whole.
   *
   * Two cards open pages of their own rather than catalogue ids (2026-10-12):
   * Payments taken by employee reads payments by when they were taken, and the
   * box resets are the box balance's history, read-only since the cash drawer
   * replaced it.
   */
  const query = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const matches = (card: ReportCard) => !query || card.title.toLowerCase().includes(query);
    const live = (catalogue?.sections ?? []).map((entry) => ({
      key: entry.section as string,
      heading: t(`reports.tabs.${entry.section}`),
      archived: false,
      cards: entry.reports
        .map(
          (report): ReportCard => ({
            key: `live:${report.id}`,
            to: `/reports/${report.id}`,
            title: t(reportTitleKey(report.id), report.title),
            caption: null,
          }),
        )
        .filter(matches),
    }));
    const cashCards: ReportCard[] = [
      {
        key: 'page:payments-by-employee',
        to: PAYMENTS_BY_EMPLOYEE_PATH,
        title: t('reports.paymentsByEmployee.title'),
        caption: null,
      },
    ];
    const archivedCards: ReportCard[] = [
      {
        key: 'page:box-resets',
        to: BOX_RESETS_ARCHIVE_PATH,
        title: t('reports.boxResets.title'),
        caption: t('reports.boxResets.caption'),
      },
      ...legacy.map(
        (report): ReportCard => ({
          key: `legacy:${report.id}`,
          to: `/reports/archived/${report.id}`,
          title: report.name,
          caption:
            report.periodFrom && report.periodTo
              ? `${report.periodFrom} — ${report.periodTo}`
              : t('reports.legacy.archived'),
        }),
      ),
    ];
    return [
      ...live,
      {
        key: 'cash',
        heading: t('reports.tabs.cash'),
        archived: false,
        cards: cashCards.filter(matches),
      },
      {
        key: 'archived',
        heading: t('reports.tabs.archived'),
        archived: true,
        cards: archivedCards.filter(matches),
      },
    ].filter((group) => group.cards.length > 0);
  }, [catalogue, legacy, query, t]);

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('reports.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('reports.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 px-4 pt-4">
        <h1 className="text-xl font-semibold text-content">{t('reports.title')}</h1>
        <div className="flex-1" />
        <Link
          to="/reports/overall"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          <BarChart3 className="h-4 w-4" aria-hidden />
          {t('reports.overallReport')}
          <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Link>
      </header>

      {catalogueError ? (
        <p role="alert" className="px-4 pt-3 text-sm text-danger">
          {catalogueError}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-6 pt-4">
        <div className="relative mb-3 max-w-md">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.8rem' }}
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('reports.searchPlaceholder')}
            className="w-full rounded-full border border-subtle bg-input py-2 pe-4 ps-9 text-sm text-content outline-none placeholder:text-content-muted focus:border-primary"
          />
        </div>

        {/* Straight back into whatever was read last — the repeat trip is the
            common one, and it should cost a single tap. */}
        {lastViewed && !query ? (
          <button
            type="button"
            onClick={() => open(lastViewed)}
            className="mb-4 flex w-full max-w-md items-center gap-2.5 rounded-card border border-subtle bg-raised px-3 py-2.5 text-start transition-colors hover:border-primary"
          >
            <Clock3 className="h-4 w-4 shrink-0 text-content-muted" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-content-muted">
                {t('reports.lastViewed')}
              </span>
              <span className="block truncate text-sm font-medium text-content">
                {lastViewed.title}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-content-secondary rtl:rotate-180" aria-hidden />
          </button>
        ) : null}

        {groups.length === 0 ? (
          <p className="py-2 text-sm text-content-muted">{t('common.noResults')}</p>
        ) : null}

        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.key}>
              <p
                className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] ${
                  group.archived ? 'text-secondary/80' : 'text-content-muted'
                }`}
              >
                {group.heading}
              </p>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {group.cards.map((card) => (
                  <button
                    key={card.key}
                    type="button"
                    data-report-path={card.to}
                    onClick={() => navigate(card.to)}
                    className={`flex items-center gap-2 rounded-card border bg-raised px-3 py-2.5 text-start text-sm transition-colors ${
                      group.archived
                        ? // Archived cards carry the archive's violet on touch.
                          'border-subtle text-content-secondary hover:border-secondary hover:text-content'
                        : 'border-subtle text-content-secondary hover:border-primary hover:text-content'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{card.title}</span>
                      {card.caption ? (
                        <span className="mt-0.5 block truncate text-xs text-content-muted">
                          {card.caption}
                        </span>
                      ) : null}
                    </span>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-content-muted rtl:rotate-180"
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
